import type { HTMLProps } from 'react';
import { clsx } from 'clsx';
import XIcon from '../../icons/react/icon-x';
import { PlainIconButton } from '../button';
import { Typography, type TypographyTone } from '../typography';
import styles from './page-banner.module.scss';

export type PageBannerVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'danger'
  | 'green'
  | 'success'
  | 'dark'
  | 'neutral'
  | 'neutral-lt';

const textToneByVariant: Record<PageBannerVariant, TypographyTone> = {
  primary: 'primary-dk',
  secondary: 'secondary-dk',
  tertiary: 'tertiary-dk',
  quaternary: 'quaternary-dk',
  danger: 'danger-dk',
  success: 'success-dk',
  dark: 'background',
  neutral: 'neutral-dk',
  'neutral-lt': 'md',
  green: 'green-dk',
};

export interface PageBannerProps extends HTMLProps<HTMLDivElement> {
  onDismiss?: VoidFunction;
  variant?: PageBannerVariant;
}

export function PageBanner({ children, className, onDismiss, variant = 'primary', ...rest }: PageBannerProps) {
  return (
    <div className={clsx(styles.pageBanner, onDismiss ? styles.withDismiss : undefined, styles[`variant-${variant}`], className)} {...rest}>
      <svg aria-hidden className={styles.before} fill="none" height="8" viewBox="0 0 8 8" width="8" xmlns="http://www.w3.org/2000/svg">
        <title>Decorative curve</title>
        <path d="M8 0H0V8C0 3.58172 3.58167 0 8 0Z" />
      </svg>

      <Typography lineHeight="1rem" size="0.75rem" tone={textToneByVariant[variant]}>
        {children}
      </Typography>

      <svg aria-hidden className={styles.after} fill="none" height="8" viewBox="0 0 8 8" width="8" xmlns="http://www.w3.org/2000/svg">
        <title>Decorative curve</title>
        <path d="M0 0H8V8C8 3.58172 4.41833 0 0 0Z" />
      </svg>

      {onDismiss ? (
        <PlainIconButton className={styles.dismissButton} onClick={onDismiss} tone={textToneByVariant[variant]}>
          <XIcon aria-label="Close banner" width={16} />
        </PlainIconButton>
      ) : null}
    </div>
  );
}
